import { useEffect, useState, useCallback } from "react";
import api, { apiError, SERVICE_OPTIONS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Printer } from "lucide-react";

const ROLE_LABELS = {
  super_admin: "TAG Super Admin",
  client_admin: "Client Admin",
  provider: "Provider",
};

const ROLE_COLORS = {
  super_admin: "bg-slate-900 text-white hover:bg-slate-900",
  client_admin: "bg-blue-100 text-blue-700 hover:bg-blue-100",
  provider: "bg-green-100 text-green-700 hover:bg-green-100",
};

const EMPTY = { email: "", password: "", name: "", role: "", client_id: "", school_id: "", services: [], title: "" };

export default function UsersPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [welcome, setWelcome] = useState(null);
  const [tempPw, setTempPw] = useState("");
  const [caseload, setCaseload] = useState([]);

  const load = useCallback(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, [load]);

  const roleOptions = isSuper
    ? ["super_admin", "client_admin", "provider"]
    : ["client_admin", "provider"];

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const toggleSvc = (svc) => setForm({ ...form, services: form.services.includes(svc) ? form.services.filter((s) => s !== svc) : [...form.services, svc] });

  const portalLink = (u) => {
    const c = clients.find((x) => x.id === u.client_id);
    return c?.subdomain ? `${window.location.origin}/${c.subdomain}` : window.location.origin;
  };

  const openWelcome = async (u) => {
    setTempPw("");
    setWelcome(u);
    try {
      const params = isSuper && u.client_id ? { client_id: u.client_id } : {};
      const r = await api.get("/students", { params });
      setCaseload(r.data.filter((s) => (s.provider_ids || []).includes(u.id)));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1.5">Access Control</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">Administrators and providers · {users.length} accounts</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-user-button" className="rounded-full bg-primary hover:bg-primary/90">
          <Plus size={15} className="mr-1.5" /> Add User
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Services</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-slate-500">{u.email}</TableCell>
                <TableCell><Badge className={`rounded-full ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</Badge></TableCell>
                <TableCell className="text-sm text-slate-600">{u.title || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{u.client_id ? clientName(u.client_id) : "All (TAG)"}</TableCell>
                <TableCell className="text-sm text-slate-600">{(u.services || []).join(", ") || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-3">
                    {u.role === "provider" && (
                      <button onClick={() => openWelcome(u)} className="text-slate-400 transition-colors duration-200 hover:text-slate-900"
                        title="Print welcome sheet" data-testid={`welcome-print-button-${u.email}`}>
                        <Printer size={15} />
                      </button>
                    )}
                    {u.role !== "super_admin" && u.id !== user.id && (
                      <button onClick={() => remove(u)} className="text-slate-400 transition-colors duration-200 hover:text-red-600" data-testid={`delete-user-${u.email}`}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role">{ROLE_LABELS[form.role]}</SelectValue></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isSuper && form.role && form.role !== "super_admin" && (
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, school_id: "" })}>
                  <SelectTrigger data-testid="user-client-select"><SelectValue placeholder="Select client">{clientName(form.client_id)}</SelectValue></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.role === "provider" && (
              <div className="space-y-1.5">
                <Label>Job title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. SPED Teacher, Speech Therapist" data-testid="user-title-input" />
              </div>
            )}
            {form.role === "provider" && (
              <div className="space-y-1.5">
                <Label>Services offered</Label>
                <div className="grid grid-cols-2 gap-2">
                  {SERVICE_OPTIONS.map((svc) => (
                    <label key={svc} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.services.includes(svc)} onCheckedChange={() => toggleSvc(svc)}
                        data-testid={`user-service-${svc.toLowerCase().replace(/ /g, "-")}`} />
                      {svc}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={save} className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="save-user-button">Create user</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!welcome} onOpenChange={(v) => !v && setWelcome(null)}>
        <DialogContent className="sm:max-w-md" data-testid="welcome-dialog">
          <DialogHeader><DialogTitle className="font-display">Provider Welcome Sheet</DialogTitle></DialogHeader>
          {welcome && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="font-semibold text-slate-900">{welcome.name}</div>
                <div className="text-slate-500">{welcome.title || "Provider"} · {welcome.email}</div>
                <div className="mt-2 font-mono text-xs text-slate-600">{portalLink(welcome)}</div>
                <div className="mt-1 text-xs text-slate-500">{caseload.length} students on caseload</div>
              </div>
              <div className="space-y-1.5">
                <Label>Temporary password (optional — printed on the sheet)</Label>
                <Input value={tempPw} onChange={(e) => setTempPw(e.target.value)}
                  placeholder="Leave blank to omit" data-testid="welcome-temp-password-input" />
              </div>
              <Button onClick={() => window.print()} className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="welcome-print-confirm">
                <Printer size={15} className="mr-1.5" /> Print welcome sheet
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {welcome && (
        <div className="print-area welcome-sheet hidden" data-testid="welcome-sheet">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #0D6E6E", paddingBottom: "14px" }}>
            <div>
              <div style={{ fontSize: "22px", fontWeight: 800, fontFamily: "Manrope, Inter, sans-serif" }}>Provider Welcome Sheet</div>
              <div style={{ fontSize: "13px", color: "#475569" }}>{clientName(welcome.client_id)} · Service Verification Portal</div>
            </div>
            <img src="/tagverify-logo.png" alt="TAGVerify" style={{ height: "26px" }} />
          </div>

          <div style={{ marginTop: "18px", fontSize: "14px", lineHeight: 1.5 }}>
            <p>Welcome, <strong>{welcome.name}</strong> ({welcome.title || "Provider"})!</p>
            <p style={{ marginTop: "6px" }}>Use TAGVerify to check students out and back in every time you provide a service. Scan the student's QR badge at pickup and again at return — that's it.</p>
          </div>

          <div style={{ marginTop: "16px", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "14px 16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748b" }}>Your access</div>
            <table style={{ marginTop: "8px", fontSize: "14px" }}>
              <tbody>
                <tr><td style={{ paddingRight: "16px", color: "#64748b" }}>Portal</td><td style={{ fontFamily: "monospace" }}>{portalLink(welcome)}</td></tr>
                <tr><td style={{ paddingRight: "16px", color: "#64748b" }}>Email</td><td style={{ fontFamily: "monospace" }}>{welcome.email}</td></tr>
                <tr><td style={{ paddingRight: "16px", color: "#64748b" }}>Password</td><td style={{ fontFamily: "monospace" }}>{tempPw || "Provided separately by your administrator"}</td></tr>
              </tbody>
            </table>
            <div style={{ marginTop: "10px", fontSize: "12px", color: "#475569" }}>
              1. Open the portal link · 2. Sign in · 3. Tap "Scan" and scan a student's QR badge to pick up — scan again to return
            </div>
          </div>

          <div style={{ marginTop: "18px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748b" }}>Your caseload · {caseload.length} students</div>
            <table style={{ marginTop: "8px", width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #cbd5e1", textAlign: "left" }}>
                  <th style={{ padding: "4px 8px 4px 0" }}>Student</th>
                  <th style={{ padding: "4px 8px" }}>ID</th>
                  <th style={{ padding: "4px 8px" }}>Grade</th>
                  <th style={{ padding: "4px 8px" }}>Teacher</th>
                  <th style={{ padding: "4px 0 4px 8px" }}>Services</th>
                </tr>
              </thead>
              <tbody>
                {caseload.map((s) => (
                  <tr key={s.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "4px 8px 4px 0", fontWeight: 600 }}>{s.first_name} {s.last_name}</td>
                    <td style={{ padding: "4px 8px", fontFamily: "monospace" }}>{s.student_id}</td>
                    <td style={{ padding: "4px 8px" }}>{s.grade}</td>
                    <td style={{ padding: "4px 8px" }}>{s.teacher || "—"}</td>
                    <td style={{ padding: "4px 0 4px 8px" }}>{(s.services || []).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "20px", fontSize: "10px", color: "#94a3b8" }}>
            Powered by TAGVerify · The Allicock Group · Keep this sheet secure — it may contain login information
          </div>
        </div>
      )}
    </div>
  );
}
